import { useState, useCallback, useMemo } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Header } from "@/components/Header";
import { IdeaFeed } from "@/components/IdeaFeed";
import { IdeaDetail } from "@/components/IdeaDetail";
import { IdeaForm } from "@/components/IdeaForm";
import { Rankings } from "@/components/Rankings";
import type { ViewMode } from "@/lib/types";
import {
	getStore,
	getIdeas,
	getComments,
	getIdea,
	voteIdea,
	addIdea,
	updateIdea,
	addComment,
	setCurrentUser,
} from "@/lib/store";
import "./index.css";

export function App() {
	const [view, setView] = useState<ViewMode>("feed");
	const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);
	const [editMode, setEditMode] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);

	const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

	const store = useMemo(() => getStore(), [refreshKey]);
	const ideas = useMemo(() => getIdeas(), [refreshKey]);

	const userMap = useMemo(() => {
		const m = new Map<string, (typeof store.users)[0]>();
		for (const u of store.users) m.set(u.id, u);
		return m;
	}, [store.users]);

	const handleNavigate = useCallback((newView: ViewMode, data?: string) => {
		setView(newView);
		setEditMode(false);
		if (data) setSelectedIdeaId(data);
		window.scrollTo(0, 0);
	}, []);

	const handleSelectIdea = useCallback((ideaId: string) => {
		setSelectedIdeaId(ideaId);
		setEditMode(false);
		setView("detail");
		window.scrollTo(0, 0);
	}, []);

	const handleVote = useCallback(
		(ideaId: string) => {
			voteIdea(ideaId, store.currentUser.id);
			refresh();
		},
		[store.currentUser.id, refresh],
	);

	const handleSwitchUser = useCallback(
		(userId: string) => {
			setCurrentUser(userId);
			refresh();
		},
		[refresh],
	);

	const handleSubmitIdea = useCallback(
		(data: {
			title: string;
			problem: string;
			solution: string;
			targetMarket: string;
			monetization: string;
			tags: string[];
			changeNote: string;
		}) => {
			addIdea({
				title: data.title,
				problem: data.problem,
				solution: data.solution,
				targetMarket: data.targetMarket,
				monetization: data.monetization,
				tags: data.tags,
				authorId: store.currentUser.id,
				forkedFromId: null,
			});
			refresh();
			setView("feed");
			window.scrollTo(0, 0);
		},
		[store.currentUser.id, refresh],
	);

	const handleForkIdea = useCallback(
		(data: {
			title: string;
			problem: string;
			solution: string;
			targetMarket: string;
			monetization: string;
			tags: string[];
			changeNote: string;
		}) => {
			addIdea({
				title: data.title,
				problem: data.problem,
				solution: data.solution,
				targetMarket: data.targetMarket,
				monetization: data.monetization,
				tags: data.tags,
				authorId: store.currentUser.id,
				forkedFromId: selectedIdeaId,
			});
			refresh();
			setView("feed");
			window.scrollTo(0, 0);
		},
		[store.currentUser.id, selectedIdeaId, refresh],
	);

	const handleEditIdea = useCallback(
		(data: {
			title: string;
			problem: string;
			solution: string;
			targetMarket: string;
			monetization: string;
			tags: string[];
			changeNote: string;
		}) => {
			if (!selectedIdeaId) return;
			updateIdea(selectedIdeaId, data, store.currentUser.id);
			refresh();
			setEditMode(false);
			setView("detail");
		},
		[selectedIdeaId, store.currentUser.id, refresh],
	);

	const handleAddComment = useCallback(
		(ideaId: string, content: string) => {
			addComment(ideaId, store.currentUser.id, content);
			refresh();
		},
		[store.currentUser.id, refresh],
	);

	const renderContent = () => {
		switch (view) {
			case "feed":
				return (
					<IdeaFeed
						ideas={ideas}
						users={store.users}
						currentUserId={store.currentUser.id}
						onVote={handleVote}
						onSelectIdea={handleSelectIdea}
					/>
				);

			case "detail": {
				const idea = selectedIdeaId ? getIdea(selectedIdeaId) : null;
				if (!idea) {
					setView("feed");
					return null;
				}
				const author = userMap.get(idea.authorId);
				const comments = getComments(idea.id);
				const forkedFrom = idea.forkedFromId
					? getIdea(idea.forkedFromId) ?? null
					: null;
				const forks = ideas.filter((i) => i.forkedFromId === idea.id);

				return (
					<IdeaDetail
						idea={idea}
						author={author}
						comments={comments}
						users={userMap}
						currentUserId={store.currentUser.id}
						forkedFrom={forkedFrom}
						forks={forks}
						onVote={handleVote}
						onBack={() => {
							setView("feed");
							window.scrollTo(0, 0);
						}}
						onFork={(ideaId) => {
							setSelectedIdeaId(ideaId);
							setView("fork");
							window.scrollTo(0, 0);
						}}
						onEdit={(ideaId) => {
							setSelectedIdeaId(ideaId);
							setEditMode(true);
							setView("submit");
							window.scrollTo(0, 0);
						}}
						onAddComment={handleAddComment}
						onSelectIdea={handleSelectIdea}
					/>
				);
			}

			case "submit": {
				if (editMode && selectedIdeaId) {
					const editIdea = getIdea(selectedIdeaId);
					if (editIdea && editIdea.authorId === store.currentUser.id) {
						return (
							<IdeaForm
								mode="edit"
								existingIdea={editIdea}
								onSubmit={handleEditIdea}
								onBack={() => {
									setEditMode(false);
									setView("detail");
									window.scrollTo(0, 0);
								}}
							/>
						);
					}
				}
				return (
					<IdeaForm
						mode="submit"
						onSubmit={handleSubmitIdea}
						onBack={() => {
							setView("feed");
							window.scrollTo(0, 0);
						}}
					/>
				);
			}

			case "fork": {
				const forkSource = selectedIdeaId
					? getIdea(selectedIdeaId)
					: null;
				return (
					<IdeaForm
						mode="fork"
						existingIdea={forkSource ?? undefined}
						onSubmit={handleForkIdea}
						onBack={() => {
							if (selectedIdeaId) {
								setView("detail");
							} else {
								setView("feed");
							}
							window.scrollTo(0, 0);
						}}
					/>
				);
			}

			case "rankings":
				return (
					<Rankings
						ideas={ideas}
						users={store.users}
						onSelectIdea={handleSelectIdea}
						onBack={() => {
							setView("feed");
							window.scrollTo(0, 0);
						}}
					/>
				);

			default:
				return null;
		}
	};

	return (
		<TooltipProvider>
			<div className="min-h-screen bg-background">
				<Header
					currentUser={store.currentUser}
					users={store.users}
					onNavigate={handleNavigate}
					onSwitchUser={handleSwitchUser}
				/>
				<main className="max-w-5xl mx-auto px-4 py-8">
					{renderContent()}
				</main>
			</div>
		</TooltipProvider>
	);
}

export default App;
